import { lazy, Suspense, useContext, useEffect, useState } from "react";
import { LaunchCtx } from "@/lib/launch";
import { Mail, Instagram } from "lucide-react";
import { Link } from "react-router-dom";
import { api, resolveImageUrl } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import EditableText from "@/components/EditableText";
import { openTicket } from "@/lib/ticketBus";
import { openSupporter, useSupporters } from "@/lib/supporters";

const SupporterHost = lazy(() => import("@/components/SupporterPerks"));   // patch 33

const BOX =
  "flex items-center justify-center w-[140px] h-[56px] md:w-[180px] md:h-[72px] p-3 shrink-0";

function trackClick(slot) {
  api.post(`/sponsors/${slot}/click`).catch(() => {});
}

function SponsorTile({ s, lang, onImg }) {
  if (s.person) {   // patch 33: a supporter without a logo → their name / @username
    return (
      <Link to={`/u/${s.username}`} data-testid={`supporter-tile-${s.username}`}
        className={`${BOX} rounded-sm gap-1.5 text-[13px] md:text-[15px] font-bold text-pink-200 hover:text-pink-100 whitespace-nowrap`}>
        <span aria-hidden className="text-pink-400">♥</span>
        <span className="truncate">{s.name}</span>
      </Link>
    );
  }
  const filled = s.active && s.logo_url;
  if (!filled) {
    return (
      <div
        data-testid={`sponsor-tile-${s.slot}`}
        className={`${BOX} rounded-sm border border-dashed border-amber-500/30`}
      >
        <span className="text-[10px] uppercase tracking-[0.18em] text-stone-600 text-center leading-tight">
          {lang === "pt" ? "A tua marca aqui" : "Your brand here"}
        </span>
      </div>
    );
  }
  const img = (
    <img
      src={resolveImageUrl(s.logo_url, 200)}
      alt={s.name || "sponsor"}
      className="max-w-full max-h-full object-contain"
      draggable={false}
      onLoad={onImg}
      onError={onImg}
    />
  );
  if (s.clickable && s.link) {
    return (
      <a
        href={s.link}
        target="_blank"
        rel="noopener sponsored"
        onClick={() => trackClick(s.slot)}
        data-testid={`sponsor-tile-${s.slot}`}
        className={`${BOX} rounded-sm cursor-pointer transition-all duration-200 hover:-translate-y-0.5 hover:brightness-125`}
      >
        {img}
      </a>
    );
  }
  return (
    <div
      data-testid={`sponsor-tile-${s.slot}`}
      className={`${BOX} rounded-sm cursor-default select-none`}
    >
      {img}
    </div>
  );
}

export default function Footer() {
  const { t, lang } = useI18n();
  const [sponsors, setSponsors] = useState([]);
  const sup = useSupporters();
  const [wantSup, setWantSup] = useState(false);
  useEffect(() => {
    const on = () => setWantSup(true);
    window.addEventListener("tier:supporter", on);
    return () => window.removeEventListener("tier:supporter", on);
  }, []);
  const instagram = useContext(LaunchCtx)?.instagram || "https://www.instagram.com/tiertcg";   // patch 42: always shown (the icon row was removed)   // patch 30: shared with the launch gate (no second request)

  useEffect(() => {
    api
      .get("/sponsors")
      .then((r) => setSponsors(r.data || []))
      .catch(() => {});
  }, []);

  // batch 21: the strip moves by half its width, so it must (1) be long enough to fill a phone
  // screen twice and (2) restart once every logo has loaded — logos arriving late used to change its
  // width mid-animation, which made it jump or leave a gap on mobile.
  const tiles = [...sponsors, ...(sup?.people || []).map((p) => ({ ...p, person: true, slot: `u-${p.username}` }))];
  const reps = tiles.length ? Math.max(1, Math.ceil(6 / tiles.length)) : 1;
  const half = Array.from({ length: reps }, () => tiles).flat();
  const loop = [...half, ...half];
  const logos = loop.filter((s) => s.active && s.logo_url).length;
  const [loaded, setLoaded] = useState(0);
  const [run, setRun] = useState(0);
  useEffect(() => {
    if (!logos) return undefined;
    if (loaded >= logos) {
      setRun((r) => r + 1);
      return undefined;
    }
    const t = setTimeout(() => setRun((r) => r + 1), 4000); // slow network: restart anyway
    return () => clearTimeout(t);
  }, [loaded, logos]);

  return (
    <footer data-testid="site-footer" className="relative border-t border-amber-500/15 bg-[#070709]">
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-14">
        <div className="text-center sm:text-left mb-8">
          <div className="text-[11px] uppercase tracking-[0.3em] font-semibold text-amber-400/90">
            <EditableText k="footer.supporters_title">{t.footer.title}</EditableText>
          </div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight mt-2 text-stone-100 leading-[0.95] flex items-center justify-center sm:justify-start gap-3 sm:gap-4">
            <EditableText k="footer.supporters_overline">{t.footer.overline}</EditableText>
            <span className="px-sprite px-megaphone" style={{ backgroundImage: "url(/pixo/megaphone-sheet.png)" }} aria-hidden data-testid="sponsors-megaphone" />
          </h2>
        </div>

        {/* Partners strip — one tile per sponsor slot, placeholders for empty slots */}
        <div
          data-testid="partners-marquee"
          className="relative overflow-hidden py-6"
          style={{
            WebkitMaskImage:
              "linear-gradient(to right, transparent 0%, #000 10%, #000 90%, transparent 100%)",
            maskImage:
              "linear-gradient(to right, transparent 0%, #000 10%, #000 90%, transparent 100%)",
          }}
        >
          <div key={run} className="partners-track flex items-center w-max" data-run={run}>
            {loop.map((s, i) => (
              <div key={`${s.slot}-${i}`} className="shrink-0 pr-10 sm:pr-16">
                <SponsorTile s={s} lang={lang} onImg={run ? undefined : () => setLoaded((n) => n + 1)} />
              </div>
            ))}
          </div>
        </div>

        <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
          <p className="text-sm text-stone-400 max-w-xl leading-relaxed">
            <EditableText k="footer.supporters_lede" multiline>{t.footer.lede}</EditableText>
          </p>
          <div className="flex flex-col sm:items-end gap-2 shrink-0">
            <button
              type="button"
              onClick={openSupporter}
              data-testid="sponsor-apply-button"
              className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] font-bold text-amber-400 hover:text-amber-200 w-max"
            >
              <Mail className="w-3.5 h-3.5" />
              <EditableText k="footer.supporters_cta">{t.footer.cta}</EditableText>
            </button>
          </div>
        </div>

        {wantSup && <Suspense fallback={null}><SupporterHost key="sup" /></Suspense>}

        <div className="gold-divider my-10" />

        <div className="flex flex-wrap items-center justify-center gap-3 mb-6" data-testid="footer-social">
          <button
            type="button"
            onClick={() => openTicket()}
            data-testid="footer-contact"
            className="inline-flex items-center gap-2 border border-amber-500/30 hover:border-amber-400/70 text-amber-200 px-4 py-2 rounded-sm text-[11px] font-bold uppercase tracking-[0.18em]"
          >
            <Mail className="w-3.5 h-3.5" /> {t.footer.contact}
          </button>
          {instagram && (
            <a
              href={instagram}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-instagram"
              className="inline-flex items-center gap-2 border border-pink-400/40 hover:border-pink-300/80 text-pink-200 px-4 py-2 rounded-sm text-[11px] font-bold uppercase tracking-[0.18em]"
            >
              <Instagram className="w-3.5 h-3.5" /> Instagram
            </a>
          )}
        </div>

        <nav
          data-testid="footer-legal-nav"
          className="flex flex-wrap items-center justify-center gap-x-3 gap-y-0 text-[10px] uppercase tracking-[0.2em] font-medium text-stone-500 mb-6"
        >
          {[
            { to: "/funcionalidades", key: "features", label: t.feat?.nav },
            { to: "/como-avaliamos", key: "rate", label: t.rate?.nav },
            { to: "/about", key: "about" },
            { to: "/terms", key: "terms" },
            { to: "/privacy", key: "privacy" },
            { to: "/contact", key: "contact" },
          ].map((l) => (
            <Link
              key={l.key}
              to={l.to}
              data-testid={`footer-legal-${l.key}`}
              className="inline-flex items-center min-h-[36px] px-1 hover:text-stone-300 transition-colors"
            >
              {l.label || t.legal?.[l.key]?.title || l.key}
            </Link>
          ))}
        </nav>

        <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-stone-500">
          <div className="flex items-center gap-3">
            <img src="/brand/tiertcg-logo.webp" alt="tierTCG" className="h-5 w-auto" />
            <span>
              {t.footer.copyright} · © {new Date().getFullYear()}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span>{<EditableText k="footer.tagline">{t.footer.tagline}</EditableText>}</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
